import { Box, Stack } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import {
  overbarOffset,
  VideoTimelineEvent,
} from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimelineEvent";
import { VideoTimelineAreaTrack } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimelineAreaTrack";
import { Dayjs } from "dayjs";
import React from "react";

interface EventTimelineTrackProps {
  events: ScrapEyesEvent[];
  videoStart: Dayjs;
  videoDuration: number;
  currentTime: number;
  onMouseDown?: (e: React.MouseEvent) => void;
  onEventClick?: (eventId: string) => void;
  // Restricts which events invoke onEventClick and show the clickable
  // pointer/hover styling — omitted, every event does.
  clickableEventIds?: ReadonlySet<string>;
  selectedEventId?: string | null;
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
  eventHeight?: number;
  overbarHeight?: number;
  showPlayhead?: boolean;
}

export const EventTimelineTrack = React.memo(
  ({
    events,
    videoStart,
    videoDuration,
    currentTime,
    onMouseDown,
    onEventClick,
    clickableEventIds,
    selectedEventId = null,
    highlightColorsByEventId,
    eventHeight = 8,
    overbarHeight = 20,
    showPlayhead = true,
  }: EventTimelineTrackProps) => {
    const playheadPosition = (currentTime / videoDuration) * 100;

    return (
      <Box
        sx={{
          position: "relative",
          cursor: onMouseDown ? "pointer" : "default",
          userSelect: "none",
        }}
        onMouseDown={onMouseDown}
      >
        {/* Clips a bar's horizontal bleed past the track edges without
            clipping the connector's vertical space above/below it; the
            playhead's handle overhangs the edges, so it stays outside this
            wrapper. */}
        <Box sx={{ overflow: "hidden" }}>
          <Stack spacing={0}>
            <VideoTimelineAreaTrack
              events={events}
              eventType="lift"
              videoStart={videoStart}
              duration={videoDuration}
            />
            <Box sx={{ height: 2 }} />
            <Box
              sx={{
                position: "relative",
                height: eventHeight,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: overbarOffset(overbarHeight),
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: eventHeight,
                }}
              >
                {events.map((event, index) => {
                  const eventStartSec = event.start.diff(videoStart, "second");
                  const eventEndSec = event.end.diff(videoStart, "second");
                  const isClickable =
                    clickableEventIds === undefined ||
                    clickableEventIds.has(event.id);
                  return (
                    <VideoTimelineEvent
                      key={event.id}
                      event={event}
                      index={index}
                      selectedEventId={selectedEventId}
                      onEventClick={isClickable ? onEventClick : undefined}
                      highlightColor={highlightColorsByEventId?.get(event.id)}
                      allEvents={events}
                      videoStart={videoStart}
                      duration={videoDuration}
                      overbarHeight={overbarHeight}
                      isActive={
                        currentTime >= eventStartSec &&
                        currentTime <= eventEndSec
                      }
                    />
                  );
                })}
              </Box>
            </Box>
            <Box sx={{ height: overbarOffset(overbarHeight) }} />
            <Box sx={{ height: 4 }} />
            <VideoTimelineAreaTrack
              events={events}
              eventType="drop"
              videoStart={videoStart}
              duration={videoDuration}
            />
          </Stack>
        </Box>
        {showPlayhead ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${playheadPosition}%`,
              width: 2,
              bgcolor: "error.main",
              pointerEvents: "none",
              zIndex: 20,
              boxShadow: "0 0 4px rgba(211, 47, 47, 0.5)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -5,
                left: -5,
                width: 10,
                height: 10,
                bgcolor: "error.main",
                borderRadius: "50%",
                border: "2px solid white",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            />
          </Box>
        ) : null}
      </Box>
    );
  },
  (prev, next) => {
    if (prev.events !== next.events) return false;
    if (!prev.videoStart.isSame(next.videoStart)) return false;
    if (prev.videoDuration !== next.videoDuration) return false;
    if (prev.onMouseDown !== next.onMouseDown) return false;
    if (prev.onEventClick !== next.onEventClick) return false;
    if (prev.clickableEventIds !== next.clickableEventIds) return false;
    if (prev.selectedEventId !== next.selectedEventId) return false;
    if (prev.highlightColorsByEventId !== next.highlightColorsByEventId)
      return false;
    if (prev.eventHeight !== next.eventHeight) return false;
    if (prev.overbarHeight !== next.overbarHeight) return false;

    const prevShowPlayhead = prev.showPlayhead ?? true;
    const nextShowPlayhead = next.showPlayhead ?? true;
    if (prevShowPlayhead !== nextShowPlayhead) return false;

    if (prevShowPlayhead && prev.currentTime !== next.currentTime) return false;

    if (!prevShowPlayhead) {
      for (const event of prev.events) {
        const startSec = event.start.diff(prev.videoStart, "second");
        const endSec = event.end.diff(prev.videoStart, "second");
        const prevActive =
          prev.currentTime >= startSec && prev.currentTime <= endSec;
        const nextActive =
          next.currentTime >= startSec && next.currentTime <= endSec;
        if (prevActive !== nextActive) return false;
      }
    }

    return true;
  }
);
